import { apiClient } from '@/services/api-client/apiClient';
import { tokenStorage } from '@/services/storage/tokenStorage';

export interface LoginPayload {
  email: string;
  password: string;
}

// Actual API shape: { success: boolean, token: string|null, data: {...}|null, error: {error: string}|null }
interface RawLoginResponse {
  success: boolean | 'true' | 'false';
  token?: string | null;
  data?: {
    jwtToken?: string;
    token?: string;
    refreshToken?: string;
    userAccountId?: string;
    fid?: string;
    name?: string;
    email?: string;
    username?: string;
    role?: string;
    department?: string;
    partnerId?: string;
  } | null;
  error?: { error?: string; message?: string } | null;
  Resp_code?: string;
  Resp_desc?: string;
}

export interface CurrentUser {
  fid: string;
  name: string;
  email: string;
  role: string;
  department?: string;
  partnerId?: string;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export async function login(payload: LoginPayload): Promise<CurrentUser> {
  const raw = await apiClient.post<RawLoginResponse>('api/v1/auth/login', payload) as unknown as RawLoginResponse;

  const isSuccess = raw.success === true || raw.success === 'true' || raw.Resp_code === 'RCS';
  // Token can be at top level or nested in data
  const tokenValue = raw.token ?? raw.data?.jwtToken ?? raw.data?.token;

  if (!isSuccess || !tokenValue) {
    const msg =
      raw.error?.error ||
      raw.error?.message ||
      raw.Resp_desc ||
      'Login failed. Please check your credentials.';
    throw new Error(msg);
  }

  tokenStorage.setToken(tokenValue);

  const d = raw.data ?? {};
  if (d.refreshToken) {
    tokenStorage.setRefreshToken(d.refreshToken);
  }

  const jwtClaims = parseJwtPayload(tokenValue) || {};
  const jwtRole = (jwtClaims.roles as string) || (jwtClaims.role as string) || '';
  const jwtEmail = (jwtClaims.sub as string) || payload.email;

  const email = d.email ?? d.username ?? jwtEmail;
  const rawName = d.name ?? (email.includes('@') ? email.split('@')[0] : email);
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);

  const user: CurrentUser = {
    fid: d.fid ?? d.userAccountId ?? (jwtClaims.accountId != null ? String(jwtClaims.accountId) : ''),
    name,
    email,
    role: d.role || jwtRole,
    department: d.department || (jwtClaims.department as string) || undefined,
    partnerId: d.partnerId,
  };
  tokenStorage.setUser(user);
  return user;
}

// There is no server-side session/refresh-token invalidation endpoint at all (no
// `/logout` mapping exists anywhere in the backend) - the previous call was silently
// caught and did nothing. Logging out is therefore purely a client-side action: clear the
// locally stored token, refresh token, and user, which is what actually happened before
// too, minus the dead network round-trip.
export function logout(): void {
  tokenStorage.clear();
}

export function getStoredUser(): CurrentUser | null {
  return tokenStorage.getUser<CurrentUser>();
}

export function isAuthenticated(): boolean {
  return tokenStorage.getToken() !== null;
}
